import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState({
    totalDonations: 0,
    totalAmount: 0,
    pendingCount: 0,
    completedCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    const userData = localStorage.getItem('adminUser');
    
    if (!token || !userData) {
      navigate('/admin/login');
      return;
    }
    
    setUser(JSON.parse(userData));
    
    const fetchDonationsData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setDonations(data.donations);
          
          // Calculate stats
          const totalAmount = data.donations.reduce((sum, donation) => sum + parseFloat(donation.amount), 0);
          const pendingCount = data.donations.filter(d => d.status === 'pending').length;
          const completedCount = data.donations.filter(d => d.status === 'completed').length;
          
          setStats({
            totalDonations: data.donations.length,
            totalAmount,
            pendingCount,
            completedCount
          });
        } else if (response.status === 401) {
          // Token expired
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          navigate('/admin/login');
        } else {
          setError('Failed to fetch donations');
        }
      } catch (error) {
        console.error('Error fetching donations:', error);
        setError('Network error. Please check if the backend server is running.');
      } finally {
        setLoading(false);
      }
    };

    fetchDonationsData();
  }, [navigate]);

  const fetchDonations = async () => {
    // This function is now inlined in useEffect but kept for refresh button
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDonations(data.donations);
        
        // Calculate stats
        const totalAmount = data.donations.reduce((sum, donation) => sum + parseFloat(donation.amount), 0);
        const pendingCount = data.donations.filter(d => d.status === 'pending').length;
        const completedCount = data.donations.filter(d => d.status === 'completed').length;
        
        setStats({
          totalDonations: data.donations.length,
          totalAmount,
          pendingCount,
          completedCount
        });
      } else if (response.status === 401) {
        // Token expired
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to fetch donations');
      }
    } catch (error) {
      console.error('Error fetching donations:', error);
      setError('Network error. Please check if the backend server is running.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'KES',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-warning',
      completed: 'bg-success',
      failed: 'bg-danger',
      cancelled: 'bg-secondary'
    };
    return badges[status] || 'bg-secondary';
  };

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Admin Dashboard - WeFlare"
        description="WeFlare admin dashboard for managing donations and content"
        robots="noindex, nofollow"
      />
      
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
          <div className="container-fluid">
            <span className="navbar-brand">
              <i className="bi bi-speedometer2 me-2"></i>
              WeFlare Admin
            </span>
            
            <div className="navbar-nav ms-auto">
              <div className="nav-item dropdown">
                <button 
                  className="nav-link dropdown-toggle btn btn-link text-white text-decoration-none" 
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i className="bi bi-person-circle me-2"></i>
                  {user?.username}
                </button>
                <ul className="dropdown-menu">
                  <li>
                    <button className="dropdown-item" onClick={() => navigate('/admin/blog')}>
                      <i className="bi bi-journal-text me-2"></i>
                      Manage Blog
                    </button>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button className="dropdown-item" onClick={handleLogout}>
                      <i className="bi bi-box-arrow-right me-2"></i>
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </nav>

        <div className="container-fluid py-4">
          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}

          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-3">
              <div className="card bg-primary text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h4 className="card-title">{stats.totalDonations}</h4>
                      <p className="card-text">Total Donations</p>
                    </div>
                    <div className="align-self-center">
                      <i className="bi bi-heart fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card bg-success text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h4 className="card-title">KES {stats.totalAmount.toLocaleString()}</h4>
                      <p className="card-text">Total Amount</p>
                    </div>
                    <div className="align-self-center">
                      <i className="bi bi-currency-exchange fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card bg-warning text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h4 className="card-title">{stats.pendingCount}</h4>
                      <p className="card-text">Pending</p>
                    </div>
                    <div className="align-self-center">
                      <i className="bi bi-clock fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-md-3">
              <div className="card bg-info text-white">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h4 className="card-title">{stats.completedCount}</h4>
                      <p className="card-text">Completed</p>
                    </div>
                    <div className="align-self-center">
                      <i className="bi bi-check-circle fs-1"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Donations Table */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <i className="bi bi-list-ul me-2"></i>
                Recent Donations
              </h5>
              <button className="btn btn-outline-primary btn-sm" onClick={fetchDonations}>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>
            <div className="card-body">
              {donations.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bi bi-inbox fs-1 text-muted mb-3"></i>
                  <p className="text-muted">No donations found</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Phone/Email</th>
                        <th>Status</th>
                        <th>Transaction ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {donations.map((donation) => (
                        <tr key={donation.id}>
                          <td>{formatDate(donation.created_at)}</td>
                          <td>
                            <strong>{formatCurrency(donation.amount, donation.currency)}</strong>
                          </td>
                          <td>
                            <span className={`badge ${donation.payment_method === 'mpesa' ? 'bg-success' : 'bg-primary'}`}>
                              {donation.payment_method.toUpperCase()}
                            </span>
                          </td>
                          <td>{donation.phone_number || donation.email || '-'}</td>
                          <td>
                            <span className={`badge ${getStatusBadge(donation.status)}`}>
                              {donation.status.toUpperCase()}
                            </span>
                          </td>
                          <td>
                            <small className="text-muted font-monospace">
                              {donation.transaction_id?.substring(0, 20)}...
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;